import { useState, useMemo, useEffect } from "react"
import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import {
  RENTAL_YIELD_DEFAULTS,
  calculateRentalYield,
  formatCurrency,
} from "@/data/tools/rental-yield-defaults"
import { CalculatorResultCard, CalculatorSlider, CalculatorCurrencyInput, CalculatorActionsBar } from "@/components/public/calculators"
import { encodeCalculatorParams, decodeCalculatorParams } from "@/lib/calculator-url-params"
import { generateCalculatorPdf } from "@/lib/calculator-pdf"

/* ------------------------------------------------------------------ */
/* FAQ data                                                            */
/* ------------------------------------------------------------------ */

const FAQ = [
  {
    q: "What is gross yield?",
    a: "Gross yield is the annual rental income divided by the purchase price, expressed as a percentage. It gives a quick headline figure but doesn't account for running costs, void periods, or mortgage payments.",
  },
  {
    q: "What is net yield?",
    a: "Net yield deducts annual costs (management fees, maintenance, insurance, ground rent, and void period losses) from the rental income before dividing by the purchase price. It's a more realistic measure of return than gross yield.",
  },
  {
    q: "What is a good rental yield in the UK?",
    a: "A gross yield of 5\u20138% is generally considered good for UK buy-to-let. However, this varies significantly by location. London yields are typically lower (3\u20135%) while northern cities and university towns can deliver 7\u201310%+. Net yield is always more important than gross.",
  },
  {
    q: "What costs should I include?",
    a: "Key costs include: letting agent management fees (typically 8\u201312% of rent), maintenance and repairs (budget 1\u20132% of property value per year), landlord insurance, ground rent and service charges (for leasehold), and void periods (typically 2\u20134 weeks per year between tenants).",
  },
  {
    q: "How do void periods affect yield?",
    a: "Void periods, the time between tenants when the property sits empty, directly reduce your income. Even one month void per year reduces annual income by about 8%. Good letting agents who use video marketing tend to fill voids faster, minimising lost rent.",
  },
  {
    q: "What is the difference between yield and ROI?",
    a: "Yield measures annual rental income as a percentage of the property price. Return on investment (ROI) includes capital appreciation as well. A property might yield 4% in rent but appreciate 5% per year, giving a total ROI of 9%. For buy-to-let decisions, yield is the more useful metric for cash flow planning.",
  },
  {
    q: "How does mortgage interest affect net yield?",
    a: "Mortgage interest is your biggest ongoing cost and significantly impacts net yield. A \u00A3200,000 property with a 75% LTV mortgage at 5% costs roughly \u00A37,500 per year in interest alone. Always calculate net yield after mortgage costs to understand your true cash flow position.",
  },
  {
    q: "Are rental yields higher in the north or south of England?",
    a: "Generally, the north delivers higher gross yields because property prices are lower relative to rents. Cities like Liverpool, Manchester, and Leeds regularly see gross yields of 6\u20138%, while London and the south-east typically deliver 3\u20135%. However, southern properties may offer stronger capital growth.",
  },
  {
    q: "How do I calculate yield on a student let?",
    a: "Student lets are usually calculated per room rather than per property. Add up the weekly rent for all rooms, multiply by the number of let weeks (typically 42\u201348 for students), and use that as your annual income figure. Student lets often achieve higher gross yields than standard lets but may have higher management costs.",
  },
  {
    q: "What tax do I pay on rental income?",
    a: "Rental income is added to your other income and taxed at your marginal rate (20%, 40%, or 45%). You can deduct allowable expenses including letting agent fees, maintenance, insurance, and a tax credit for mortgage interest at 20%. If your rental income is below \u00A31,000, the property allowance means you don't need to declare it.",
  },
]

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function RentalYieldCalculatorPage({
  meta,
}: {
  meta: SeoMeta
}) {
  const initialParams = useMemo(() => {
    if (typeof window === "undefined") return RENTAL_YIELD_DEFAULTS
    const sp = new URLSearchParams(window.location.search)
    return decodeCalculatorParams(
      {
        purchasePrice: RENTAL_YIELD_DEFAULTS.purchasePrice,
        monthlyRent: RENTAL_YIELD_DEFAULTS.monthlyRent,
        annualManagementFees: RENTAL_YIELD_DEFAULTS.annualManagementFees,
        annualMaintenance: RENTAL_YIELD_DEFAULTS.annualMaintenance,
        annualInsurance: RENTAL_YIELD_DEFAULTS.annualInsurance,
        monthsVoid: RENTAL_YIELD_DEFAULTS.monthsVoid,
        annualGroundRent: RENTAL_YIELD_DEFAULTS.annualGroundRent,
        monthlyMortgage: RENTAL_YIELD_DEFAULTS.monthlyMortgage,
      },
      sp,
    )
  }, [])

  const [purchasePrice, setPurchasePrice] = useState<number>(initialParams.purchasePrice as number)
  const [monthlyRent, setMonthlyRent] = useState<number>(initialParams.monthlyRent as number)
  const [mgmtFees, setMgmtFees] = useState<number>(initialParams.annualManagementFees as number)
  const [maintenance, setMaintenance] = useState<number>(initialParams.annualMaintenance as number)
  const [insurance, setInsurance] = useState<number>(initialParams.annualInsurance as number)
  const [voidMonths, setVoidMonths] = useState<number>(initialParams.monthsVoid as number)
  const [groundRent, setGroundRent] = useState<number>(initialParams.annualGroundRent as number)
  const [monthlyMortgage, setMonthlyMortgage] = useState<number>(initialParams.monthlyMortgage as number)

  const result = calculateRentalYield({
    purchasePrice,
    monthlyRent,
    annualManagementFees: mgmtFees,
    annualMaintenance: maintenance,
    annualInsurance: insurance,
    monthsVoid: voidMonths,
    annualGroundRent: groundRent,
    monthlyMortgage,
  })

  useEffect(() => {
    const params = encodeCalculatorParams({
      purchasePrice, monthlyRent, annualManagementFees: mgmtFees,
      annualMaintenance: maintenance, annualInsurance: insurance,
      monthsVoid: voidMonths, annualGroundRent: groundRent, monthlyMortgage,
    })
    window.history.replaceState(null, "", `?${params}`)
  }, [purchasePrice, monthlyRent, mgmtFees, maintenance, insurance, voidMonths, groundRent, monthlyMortgage])

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}?${encodeCalculatorParams({ purchasePrice, monthlyRent, annualManagementFees: mgmtFees, annualMaintenance: maintenance, annualInsurance: insurance, monthsVoid: voidMonths, annualGroundRent: groundRent, monthlyMortgage })}`
      : ""
  const shareTitle = `Rental yield: ${result.grossYield.toFixed(2)}% gross, ${result.netYield.toFixed(2)}% net on a ${formatCurrency(purchasePrice)} property`

  const handleDownloadPdf = () =>
    generateCalculatorPdf({
      title: "Rental Yield Calculation",
      subtitle: `Buy-to-let investment analysis for a ${formatCurrency(purchasePrice)} property`,
      sections: [
        {
          title: "Yield",
          rows: [
            { label: "Gross yield", value: `${result.grossYield.toFixed(2)}%`, sub: "Before costs", accent: true },
            { label: "Net yield", value: `${result.netYield.toFixed(2)}%`, sub: "After costs, before mortgage", accent: true },
          ],
        },
        {
          title: "Cashflow",
          rows: [
            { label: "Annual rent", value: formatCurrency(result.annualRent), sub: `${12 - voidMonths} months occupied` },
            { label: "Annual costs", value: formatCurrency(result.annualCosts), sub: "Management, maintenance, etc." },
            { label: "Monthly cashflow", value: `${result.monthlyProfit >= 0 ? "+" : ""}${formatCurrency(result.monthlyProfit)}`, sub: monthlyMortgage > 0 ? "After mortgage" : "Before mortgage" },
            { label: "Annual profit", value: `${result.annualProfit >= 0 ? "+" : ""}${formatCurrency(result.annualProfit)}`, sub: monthlyMortgage > 0 ? "After all costs + mortgage" : "After all costs" },
          ],
        },
      ],
      url: shareUrl,
      generatedAt: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }),
    })

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Rental Yield Calculator",
            description: meta.description,
            applicationCategory: "FinanceApplication",
            operatingSystem: "Web",
            offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" },
            provider: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://quiteright.co.uk",
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Tools", path: "/tools" }, { name: "Rental Yield Calculator" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Calculator
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Rental yield calculator
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Calculate gross yield, net yield, and monthly cashflow for
            buy-to-let investment properties in the UK.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col gap-10 lg:flex-row lg:gap-16">
            {/* Input panel */}
            <div className="flex-1 space-y-6">
              <div>
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Property details<span className="text-red">.</span>
                </h2>
                <p className="mt-1 text-sm text-slate">
                  Enter the purchase price, rent, and running costs.
                </p>
              </div>

              <CalculatorCurrencyInput
                label="Purchase price"
                value={purchasePrice}
                onChange={setPurchasePrice}
              />

              <CalculatorCurrencyInput
                label="Monthly rent"
                value={monthlyRent}
                onChange={setMonthlyRent}
              />

              <CalculatorSlider
                label="Void periods"
                value={voidMonths}
                min={0}
                max={6}
                step={0.5}
                formatValue={(v) => `${v} month${v === 1 ? "" : "s"}/year`}
                onChange={setVoidMonths}
              />

              <div className="border-t border-stone pt-6">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                  Annual costs
                </p>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <CalculatorCurrencyInput
                    label="Management fees"
                    value={mgmtFees}
                    onChange={setMgmtFees}
                    hint="Per year"
                  />
                  <CalculatorCurrencyInput
                    label="Maintenance"
                    value={maintenance}
                    onChange={setMaintenance}
                    hint="Per year"
                  />
                  <CalculatorCurrencyInput
                    label="Insurance"
                    value={insurance}
                    onChange={setInsurance}
                    hint="Per year"
                  />
                  <CalculatorCurrencyInput
                    label="Ground rent"
                    value={groundRent}
                    onChange={setGroundRent}
                    hint="If leasehold"
                  />
                </div>
              </div>

              <div className="border-t border-stone pt-6">
                <CalculatorCurrencyInput
                  label="Monthly mortgage payment"
                  value={monthlyMortgage}
                  onChange={setMonthlyMortgage}
                  hint="Optional — to see cashflow after debt service"
                />
              </div>
            </div>

            {/* Results panel */}
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Your returns<span className="text-red">.</span>
                </h2>
                <p className="mt-1 text-sm text-slate">
                  Yield and cashflow for this investment.
                </p>
              </div>

              <CalculatorActionsBar shareUrl={shareUrl} shareTitle={shareTitle} onDownloadPdf={handleDownloadPdf} />

              <div className="grid grid-cols-2 gap-4">
                <CalculatorResultCard
                  label="Gross yield"
                  value={`${result.grossYield.toFixed(2)}%`}
                  sub="Before costs"
                  accent
                  large
                />
                <CalculatorResultCard
                  label="Net yield"
                  value={`${result.netYield.toFixed(2)}%`}
                  sub="After costs, before mortgage"
                  accent
                  large
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <CalculatorResultCard
                  label="Annual rent"
                  value={formatCurrency(result.annualRent)}
                  sub={`${12 - voidMonths} months occupied`}
                />
                <CalculatorResultCard
                  label="Annual costs"
                  value={formatCurrency(result.annualCosts)}
                  sub="Management, maintenance, etc."
                />
                <CalculatorResultCard
                  label="Monthly cashflow"
                  value={`${result.monthlyProfit >= 0 ? "+" : ""}${formatCurrency(result.monthlyProfit)}`}
                  sub={monthlyMortgage > 0 ? "After mortgage" : "Before mortgage"}
                />
                <CalculatorResultCard
                  label="Annual profit"
                  value={`${result.annualProfit >= 0 ? "+" : ""}${formatCurrency(result.annualProfit)}`}
                  sub={monthlyMortgage > 0 ? "After all costs + mortgage" : "After all costs"}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            How rental yield works<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              <strong className="text-charcoal">Gross yield</strong> is the
              simplest measure: annual rent divided by purchase price.
              It&rsquo;s useful for quick comparisons between properties but
              doesn&rsquo;t tell the full story.
            </p>
            <p>
              <strong className="text-charcoal">Net yield</strong> gives a
              more realistic picture by deducting running costs (management
              fees, maintenance, insurance, void periods) before dividing by
              the purchase price.
            </p>
            <p>
              <strong className="text-charcoal">Monthly cashflow</strong>{" "}
              shows what you actually take home after all costs and mortgage
              payments. A positive cashflow means the property covers its
              own costs; a negative figure means you&rsquo;re subsidising the
              investment from other income.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "5-8%", label: "is considered a good gross yield in the UK" },
            { stat: "73%", label: "of tenants watch a video before booking a viewing" },
            { stat: "2x", label: "faster void fill with video-marketed properties" },
            { stat: "<60s", label: "to create a letting video with QuiteRight" },
          ].map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">{item.label}</p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related tools */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related tools<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Link
              href="/tools/mortgage-calculator"
              className="group rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/20 hover:shadow-sm"
            >
              <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                Mortgage calculator
              </p>
              <p className="mt-1 text-sm text-slate">
                Calculate monthly repayments for your buy-to-let mortgage.
              </p>
            </Link>
            <Link
              href="/tools/stamp-duty-calculator"
              className="group rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/20 hover:shadow-sm"
            >
              <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                Stamp duty calculator
              </p>
              <p className="mt-1 text-sm text-slate">
                Calculate SDLT including the additional property surcharge.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Fill voids faster with video
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Letting agents who market with video fill vacancies faster and
            attract better tenants. See how QuiteRight turns your rental
            listings into scroll-stopping video ads.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/features"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-8 py-4 text-lg font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              See features
            </Link>
          </div>
          <p className="mt-5 text-sm text-warm-grey">
            14-day free trial &middot; No credit card &middot; Cancel any time
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
